import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getAuth } from 'firebase/auth';

const formatDate = (value) => {
  if (!value) return '—';
  const date = value.toDate?.() || new Date(value);
  if (!(date instanceof Date) || isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-PR', {
    month: 'short',
    day: 'numeric',
  });
};

const MAX_LEADS_PREVIEW = 8;
const MAX_READ_NOTIFICATIONS = 60;

const getTimestampKey = (value) => {
  if (!value) return '0';
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string') return value;
  if (value.seconds) {
    return `${value.seconds}${value.nanoseconds ? `_${value.nanoseconds}` : ''}`;
  }
  if (typeof value.toMillis === 'function') {
    return value.toMillis().toString();
  }
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate().getTime().toString();
    } catch (err) {
      return `${Date.now()}`;
    }
  }
  return `${value}`;
};

const buildNotificationKey = (lead) =>
  `${lead.id}_${getTimestampKey(lead.updatedAt || lead.lastContact || lead.createdAt)}`;

const SellerDashboard = () => {
  const auth = getAuth();
  const [user, setUser] = useState(() => auth.currentUser);
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllLeads, setShowAllLeads] = useState(false);
  const [readNotifications, setReadNotifications] = useState(() => new Set());
  const [showReadNotifications, setShowReadNotifications] = useState(false);
  const [dismissedNotifications, setDismissedNotifications] = useState(() => new Set());

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      setUser(firebaseUser);
    });
    return unsubscribe;
  }, [auth]);

  useEffect(() => {
    const fetchLeads = async () => {
      if (!user?.email) return;
      setLoading(true);
      setError(null);
      try {
        const email = user.email.toLowerCase();
        const leadsQuery = query(
          collection(db, 'leads'),
          where('assignedToEmail', '==', email)
        );
        const [leadsSnap, recentSnap] = await Promise.all([
          getDocs(leadsQuery),
          getDocs(
            query(
              collection(db, 'leads'),
              where('assignedToEmail', '==', email),
              orderBy('updatedAt', 'desc'),
              limit(6)
            )
          ),
        ]);

        const leadsData = leadsSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        const recentData = recentSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setAssignedLeads(leadsData);
        setRecentLeads(recentData);
      } catch (err) {
        console.error('❌ Error cargando leads del vendedor:', err);
        setError('No pudimos cargar tus leads. Intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [user?.email]);

  useEffect(() => {
    let active = true;
    const loadPreferences = async () => {
      if (!user?.uid) {
        if (active) {
          setReadNotifications(new Set());
          setDismissedNotifications(new Set());
        }
        return;
      }
      try {
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);
        if (!active) return;
        const data = snap.exists() ? snap.data() : {};
        const readKeys = Array.isArray(data?.sellerReadNotifications)
          ? data.sellerReadNotifications
          : [];
        const dismissedKeys = Array.isArray(data?.sellerDismissedNotifications)
          ? data.sellerDismissedNotifications
          : [];
        setReadNotifications(new Set(readKeys));
        setDismissedNotifications(new Set(dismissedKeys));
      } catch (err) {
        console.error('⚠️ No se pudieron cargar las preferencias del vendedor', err);
      }
    };

    loadPreferences();
    return () => {
      active = false;
    };
  }, [user?.uid]);

  const persistNotificationPrefs = useCallback(
    async (field, entries) => {
      if (!user?.uid) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        await setDoc(
          userRef,
          {
            [field]: entries.slice(-MAX_READ_NOTIFICATIONS),
          },
          { merge: true }
        );
      } catch (err) {
        console.error(`⚠️ No se pudieron guardar ${field}`, err);
      }
    },
    [user?.uid]
  );

  const persistReadNotifications = useCallback(
    (entries) => persistNotificationPrefs('sellerReadNotifications', entries),
    [persistNotificationPrefs]
  );

  const persistDismissedNotifications = useCallback(
    (entries) => persistNotificationPrefs('sellerDismissedNotifications', entries),
    [persistNotificationPrefs]
  );

  const metrics = useMemo(() => {
    const total = assignedLeads.length;
    const won = assignedLeads.filter(
      (lead) => (lead.status || '').toLowerCase() === 'convertido'
    );
    const hot = assignedLeads.filter((lead) => (lead.aiScore || 0) >= 80);
    const active = total - won.length;
    const conversionRate = total ? Math.round((won.length / total) * 100) : 0;
    return {
      total,
      won: won.length,
      hot: hot.length,
      active,
      conversionRate,
    };
  }, [assignedLeads]);

  const pipeline = useMemo(() => {
    const buckets = {};
    assignedLeads.forEach((lead) => {
      const stage = (lead.pipelineStage || lead.status || 'nuevo').toLowerCase();
      buckets[stage] = (buckets[stage] || 0) + 1;
    });
    return Object.entries(buckets)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [assignedLeads]);

  const topOpportunities = useMemo(() => {
    return [...assignedLeads]
      .filter((lead) => (lead.aiScore || 0) > 0)
      .sort((a, b) => (b.aiScore || 0) - (a.aiScore || 0))
      .slice(0, 4);
  }, [assignedLeads]);

  const statusSummary = useMemo(() => {
    const summaryMap = new Map();
    assignedLeads.forEach((lead) => {
      const status = (lead.status || 'nuevo').toLowerCase();
      const item = summaryMap.get(status) || {
        label: status.replace('_', ' '),
        status,
        count: 0,
      };
      item.count += 1;
      summaryMap.set(status, item);
    });
    return Array.from(summaryMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [assignedLeads]);

  const recentNotifications = useMemo(
    () =>
      recentLeads
        .map((lead) => ({
          ...lead,
          _notificationKey: buildNotificationKey(lead),
        }))
        .filter((lead) => !dismissedNotifications.has(lead._notificationKey)),
    [recentLeads, dismissedNotifications]
  );

  const unreadNotifications = useMemo(
    () => recentNotifications.filter((lead) => !readNotifications.has(lead._notificationKey)),
    [recentNotifications, readNotifications]
  );

  const readNotificationsList = useMemo(
    () => recentNotifications.filter((lead) => readNotifications.has(lead._notificationKey)),
    [recentNotifications, readNotifications]
  );

  const markNotificationRead = useCallback(
    (notification) => {
      if (!notification?._notificationKey) return;
      setReadNotifications((prev) => {
        if (prev.has(notification._notificationKey)) return prev;
        const next = new Set(prev);
        next.add(notification._notificationKey);
        persistReadNotifications(Array.from(next));
        return next;
      });
    },
    [persistReadNotifications]
  );

  const markAllNotificationsRead = useCallback(() => {
    if (!recentNotifications.length) return;
    setReadNotifications((prev) => {
      let changed = false;
      const next = new Set(prev);
      recentNotifications.forEach(({ _notificationKey }) => {
        if (_notificationKey && !next.has(_notificationKey)) {
          next.add(_notificationKey);
          changed = true;
        }
      });
      if (changed) {
        persistReadNotifications(Array.from(next));
        return next;
      }
      return prev;
    });
  }, [persistReadNotifications, recentNotifications]);

  const dismissNotification = useCallback(
    (notification) => {
      if (!notification?._notificationKey) return;
      setDismissedNotifications((prev) => {
        if (prev.has(notification._notificationKey)) return prev;
        const next = new Set(prev);
        next.add(notification._notificationKey);
        persistDismissedNotifications(Array.from(next));
        return next;
      });
      setReadNotifications((prev) => {
        if (!prev.has(notification._notificationKey)) return prev;
        const next = new Set(prev);
        next.delete(notification._notificationKey);
        persistReadNotifications(Array.from(next));
        return next;
      });
    },
    [persistDismissedNotifications, persistReadNotifications]
  );

  const dismissAllNotifications = useCallback(() => {
    if (!recentNotifications.length) return;
    setDismissedNotifications((prev) => {
      const next = new Set(prev);
      recentNotifications.forEach(({ _notificationKey }) => {
        if (_notificationKey) {
          next.add(_notificationKey);
        }
      });
      persistDismissedNotifications(Array.from(next));
      return next;
    });
    setReadNotifications((prev) => {
      const next = new Set(prev);
      recentNotifications.forEach(({ _notificationKey }) => {
        if (_notificationKey) {
          next.delete(_notificationKey);
        }
      });
      persistReadNotifications(Array.from(next));
      return next;
    });
  }, [persistDismissedNotifications, persistReadNotifications, recentNotifications]);

  const displayedLeads = showAllLeads
    ? assignedLeads
    : assignedLeads.slice(0, MAX_LEADS_PREVIEW);
  const hiddenLeadCount = Math.max(assignedLeads.length - MAX_LEADS_PREVIEW, 0);
  const canToggleLeadView = assignedLeads.length > MAX_LEADS_PREVIEW;

  if (!user) {
    return <p className="text-gray-600">Inicia sesión para ver tu panel.</p>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">
          Hola, {user.displayName || user.email}
        </h2>
        <p className="text-gray-500">
          Este es el estado de tus leads asignados y tu avance mensual.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded">
          {error}
        </div>
      )}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Leads asignados</p>
          <p className="text-3xl font-semibold text-gray-900">{metrics.total}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">En proceso</p>
          <p className="text-3xl font-semibold text-blue-600">{metrics.active}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Hot leads</p>
          <p className="text-3xl font-semibold text-orange-500">{metrics.hot}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-500">Tasa de cierre</p>
          <p className="text-3xl font-semibold text-emerald-600">
            {metrics.conversionRate}%
          </p>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline</h3>
          {pipeline.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aún no tienes leads con etapas asignadas.
            </p>
          ) : (
            <ul className="space-y-2">
              {pipeline.map(([stage, count]) => (
                <li
                  key={stage}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="capitalize text-gray-600">
                    {stage.replace('_', ' ')}
                  </span>
                  <span className="font-semibold text-gray-900">{count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Oportunidades prioridad
          </h3>
          {topOpportunities.length === 0 ? (
            <p className="text-sm text-gray-500">
              No hay leads con score de IA todavía.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {topOpportunities.map((lead) => (
                <li key={lead.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{lead.name}</p>
                    <p className="text-xs text-gray-500">{lead.businessType || lead.source}</p>
                  </div>
                  <span className="text-sm font-semibold text-orange-600">
                    Score {lead.aiScore}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Mis leads</h3>
            <p className="text-sm text-gray-500">Resumen compacto de tus oportunidades activas.</p>
          </div>
          {canToggleLeadView && (
            <button
              type="button"
              onClick={() => setShowAllLeads((prev) => !prev)}
              className="text-sm font-semibold text-orange-600 hover:text-orange-700"
            >
              {showAllLeads ? 'Ver menos' : `Ver ${hiddenLeadCount} más`}
            </button>
          )}
        </div>
        <div className="mt-4 space-y-4">
          {statusSummary.length > 0 && (
            <div className="overflow-x-auto">
              <div className="flex gap-3">
                {statusSummary.map((item) => (
                  <div
                    key={item.status}
                    className="min-w-[120px] rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-xs"
                  >
                    <p className="font-semibold text-gray-700 capitalize">{item.label}</p>
                    <p className="text-gray-500">{item.count} lead{item.count === 1 ? '' : 's'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <p className="text-sm text-gray-500">Cargando tus leads...</p>
          ) : assignedLeads.length === 0 ? (
            <p className="text-sm text-gray-500">Aún no tienes leads asignados.</p>
          ) : (
            <>
              <div className="hidden md:block max-h-96 overflow-y-auto rounded-xl border border-gray-100">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-gray-600">Lead</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-600">Estado</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-600">Último contacto</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-600">Score</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {displayedLeads.map((lead) => (
                      <tr key={lead.id}>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900">{lead.name || '—'}</p>
                          <p className="text-gray-500">{lead.email || 'Sin correo'}</p>
                        </td>
                        <td className="px-4 py-3 capitalize text-gray-700">
                          {(lead.status || 'nuevo').replace('_', ' ')}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDate(lead.updatedAt || lead.lastContact)}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{lead.aiScore ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3 max-h-96 overflow-y-auto pr-1">
                {displayedLeads.map((lead) => (
                  <div
                    key={lead.id}
                    className="rounded-xl border border-gray-100 p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">{lead.name || '—'}</p>
                        <p className="text-xs text-gray-500">{lead.email || 'Sin correo'}</p>
                      </div>
                      {lead.aiScore ? (
                        <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                          Score {lead.aiScore}
                        </span>
                      ) : null}
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div>
                        <dt className="font-semibold text-gray-500">Estado</dt>
                        <dd className="capitalize text-gray-800">
                          {(lead.status || 'nuevo').replace('_', ' ')}
                        </dd>
                      </div>
                      <div>
                        <dt className="font-semibold text-gray-500">Último contacto</dt>
                        <dd>{formatDate(lead.updatedAt || lead.lastContact)}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Notificaciones recientes</h3>
            <p className="text-sm text-gray-500">Últimos movimientos de tus leads asignados.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            {recentNotifications.length > 0 && (
              <button
                type="button"
                onClick={dismissAllNotifications}
                className="font-semibold text-gray-500 hover:text-gray-700"
              >
                Ocultar todo
              </button>
            )}
            {unreadNotifications.length > 0 && (
              <button
                type="button"
                onClick={markAllNotificationsRead}
                className="font-semibold text-orange-600 hover:text-orange-700"
              >
                Marcar todo como leído
              </button>
            )}
          </div>
        </div>
        {recentNotifications.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">
            Aquí verás los leads más recientes asignados o actualizados.
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            {unreadNotifications.length === 0 ? (
              <p className="text-sm text-gray-500">No tienes notificaciones nuevas.</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100 text-sm overflow-hidden">
                {unreadNotifications.map((lead) => (
                  <li
                    key={lead._notificationKey || lead.id}
                    className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{lead.name || 'Lead sin nombre'}</p>
                      <p className="text-xs text-gray-500">
                        {(lead.pipelineStage || lead.status || 'nuevo').replace('_', ' ')}
                      </p>
                      <p className="text-xs text-gray-400">
                        Actualizado {formatDate(lead.updatedAt || lead.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <button
                        type="button"
                        onClick={() => markNotificationRead(lead)}
                        className="font-semibold text-orange-600 hover:text-orange-700"
                      >
                        Marcar leído
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissNotification(lead)}
                        className="font-semibold text-gray-500 hover:text-gray-700"
                      >
                        Ocultar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {readNotificationsList.length > 0 && (
              <div className="rounded-xl border border-dashed border-gray-200 p-4">
                <button
                  type="button"
                  onClick={() => setShowReadNotifications((prev) => !prev)}
                  className="flex w-full items-center justify-between text-sm font-medium text-gray-600"
                >
                  <span>
                    {showReadNotifications ? 'Ocultar' : 'Mostrar'} notificaciones leídas ({
                      readNotificationsList.length
                    })
                  </span>
                  <span className="text-xs text-gray-400">
                    {showReadNotifications ? '▲' : '▼'}
                  </span>
                </button>
                {showReadNotifications && (
                  <ul className="mt-3 divide-y divide-gray-100 text-sm">
                    {readNotificationsList.map((lead) => (
                      <li
                        key={`${lead._notificationKey || lead.id}-read`}
                        className="flex flex-col gap-2 py-2 md:flex-row md:items-center md:justify-between"
                      >
                        <div>
                          <p className="font-medium text-gray-800">{lead.name || 'Lead sin nombre'}</p>
                          <p className="text-xs text-gray-500">
                            {(lead.pipelineStage || lead.status || 'nuevo').replace('_', ' ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <span>{formatDate(lead.updatedAt || lead.createdAt)}</span>
                          <button
                            type="button"
                            onClick={() => dismissNotification(lead)}
                            className="font-semibold text-gray-500 hover:text-gray-700"
                          >
                            Ocultar
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default SellerDashboard;
